const BehaviorDoc = () => {
  return (
    <section className="mb-20 text-slate-300">
      <h2 className="text-3xl font-bold text-white mb-6">
        AxisNet Behaviors — Comprehensive Pipeline Documentation
      </h2>

      <p className="mb-6">
        AxisNet Behaviors extend the MediatR pipeline with cross-cutting
        concerns such as authorization, session validation, caching, logging,
        localization, validation, transactions, Recaptcha, and XSS prevention.
        Each behavior runs before or after the request handler and may
        transform, validate, or intercept requests. Below is a complete
        explanation of each one.
      </p>

      {/* AUTHORIZATION BEHAVIOR */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-10">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          AuthorizationBehavior — Role & Permission Enforcement
        </h3>

        <p className="mb-4">
          This behavior enforces access control rules declared through the{" "}
          <strong>AuthorizationAttribute</strong>. It reads both requested roles
          and permissions and compares them with the authenticated user’s
          claims.
        </p>

        <h4 className="text-lg text-white font-semibold mb-2">
          What It Does Step-by-Step
        </h4>

        <ul className="list-disc pl-6 space-y-2">
          <li>
            Extracts the <strong>AuthorizationAttribute</strong> from the
            request type.
          </li>
          <li>
            If the request does not define this attribute, the pipeline
            continues normally.
          </li>
          <li>
            Verifies that the user is authenticated; otherwise throws an
            unauthorized error.
          </li>
          <li>Reads user role claims from JWT.</li>
          <li>
            If permissions are required, ensures the user has permission claims.
          </li>
          <li>Splits both role and permission lists into collections.</li>
          <li>Checks if the user has at least one matching role.</li>
          <li>
            If permissions are included, verifies that at least one required
            permission is present.
          </li>
          <li>
            If validation succeeds, the request is allowed to proceed to the
            next handler.
          </li>
          <li>If validation fails, throws an AuthorizationException.</li>
        </ul>

        <p className="mt-4">
          This behavior enables advanced RBAC and PBAC security without
          requiring explicit authorization logic inside handlers.
        </p>
      </div>

      {/* LOCALIZATION BEHAVIOR */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-10">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          LocalizationBehavior — Culture Resolution
        </h3>

        <p className="mb-4">
          This behavior determines the culture of the request based on the
          <strong>Accept-Language</strong> header. It ensures that all error
          messages, validation texts, and domain translations match the user's
          preferred language.
        </p>

        <h4 className="text-lg text-white font-semibold mb-2">
          Execution Flow
        </h4>

        <ul className="list-disc pl-6 space-y-2">
          <li>Reads the “Accept-Language” HTTP header.</li>
          <li>
            Attempts to set <strong>CurrentCulture</strong> and{" "}
            <strong>CurrentUICulture</strong> accordingly.
          </li>
          <li>If the language is invalid, gracefully falls back to English.</li>
          <li>Passes control to the next behavior or handler.</li>
        </ul>
      </div>

      {/* LOG BEHAVIOR */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-10">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          LogBehavior — Request Tracking & Diagnostics
        </h3>

        <p className="mb-4">
          This behavior logs every request, capturing performance metrics,
          request metadata, user identity, environment info, and exception
          details. It ensures full visibility into system operations.
        </p>

        <h4 className="text-lg text-white font-semibold mb-2">
          What Gets Logged
        </h4>

        <ul className="list-disc pl-6 space-y-2">
          <li>Request type name (Category)</li>
          <li>Namespace of the handler (Source)</li>
          <li>Request path, method, IP address</li>
          <li>Current environment (Development / Production)</li>
          <li>Sanitized request payload (sensitive fields masked)</li>
          <li>Execution time in milliseconds</li>
          <li>HTTP status code</li>
          <li>Exception message and stack trace on failure</li>
        </ul>

        <h4 className="text-lg text-white font-semibold mb-2 mt-4">
          Execution Flow
        </h4>

        <ul className="list-disc pl-6 space-y-2">
          <li>Starts a timer before the request executes.</li>
          <li>Constructs a log entry containing metadata.</li>
          <li>Runs the request handler.</li>
          <li>
            On success: marks log as Information and stores execution time.
          </li>
          <li>On failure: marks log as Error and records exception details.</li>
          <li>Saves the log entry to the database.</li>
        </ul>
      </div>

      {/* RECAPTCHA BEHAVIOR */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-10">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          RecaptchaBehavior — Bot Protection
        </h3>

        <p className="mb-4">
          This behavior validates Google Recaptcha tokens for requests that
          implement <strong>IRecaptchaRequest</strong>. It protects endpoints
          against automated submissions and bot attacks.
        </p>

        <h4 className="text-lg text-white font-semibold mb-2">
          Execution Steps
        </h4>

        <ul className="list-disc pl-6 space-y-2">
          <li>Checks whether the request implements IRecaptchaRequest.</li>
          <li>Extracts the recaptcha token from the request.</li>
          <li>Validates the token using the Recaptcha service.</li>
          <li>If validation fails, throws an UnauthorizedAccessException.</li>
          <li>If successful, continues execution normally.</li>
        </ul>
      </div>

      {/* SESSION VALIDATION BEHAVIOR */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-10">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          SessionValidationBehavior — Active Session Enforcement
        </h3>

        <p className="mb-4">
          This behavior ensures that the incoming request is associated with a
          valid, non-expired, non-mismatched user session. It is essential for
          multi-device security and token consistency.
        </p>

        <h4 className="text-lg text-white font-semibold mb-2">
          Detailed Steps
        </h4>

        <ul className="list-disc pl-6 space-y-2">
          <li>
            Checks for the <strong>BypassSessionValidationAttribute</strong>.
          </li>
          <li>If bypassed, skips all session checks.</li>
          <li>Verifies the user is authenticated.</li>
          <li>Extracts session ID from the user’s JWT.</li>
          <li>Ensures the session exists in the database.</li>
          <li>Validates current IP address matches session’s IP.</li>
          <li>Ensures AccessToken and RefreshToken are still valid.</li>
          <li>If everything is valid, allows the request to continue.</li>
          <li>
            Otherwise, throws relevant session or authorization exceptions.
          </li>
        </ul>
      </div>

      {/* TRANSACTIONAL BEHAVIOR */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-10">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          TransactionalBehavior — Atomic Request Execution
        </h3>

        <p className="mb-4">
          Ensures the entire request is executed inside a{" "}
          <strong>TransactionScope</strong>. If the handler throws an exception,
          all database changes are rolled back automatically, ensuring data
          consistency.
        </p>

        <h4 className="text-lg text-white font-semibold mb-2">Workflow</h4>

        <ul className="list-disc pl-6 space-y-2">
          <li>Begins a transaction before executing the handler.</li>
          <li>Runs the request handler.</li>
          <li>If the handler succeeds, commits the transaction.</li>
          <li>If the handler fails, the transaction is rolled back.</li>
          <li>Propagates the exception upward.</li>
        </ul>
      </div>

      {/* VALIDATION BEHAVIOR */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-10">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          ValidationBehavior — Pre-Execution Request Validation
        </h3>

        <p className="mb-4">
          Executes FluentValidation validators before the request handler runs.
          It prevents invalid data from reaching the business logic.
        </p>

        <h4 className="text-lg text-white font-semibold mb-2">Workflow</h4>

        <ul className="list-disc pl-6 space-y-2">
          <li>Finds all validators registered for the request type.</li>
          <li>Runs validation rules against the request object.</li>
          <li>Aggregates errors by property.</li>
          <li>If any failure exists, throws a ValidationException.</li>
          <li>If no failures, continues the pipeline normally.</li>
        </ul>
      </div>

      {/* XSS PREVENT BEHAVIOR */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-10 mb-20">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          XssPreventBehavior — Input Sanitization
        </h3>

        <p className="mb-4">
          Prevents harmful HTML, script tags, and encoded attack vectors from
          entering the system by sanitizing all string properties in the
          request.
        </p>

        <h4 className="text-lg text-white font-semibold mb-2">
          Sanitization Process
        </h4>

        <ul className="list-disc pl-6 space-y-2">
          <li>Scans all string properties of the request.</li>
          <li>Removes inline event handlers (onclick, onload, etc.).</li>
          <li>Strips script, iframe, object, and embed tags.</li>
          <li>
            Blocks javascript:, vbscript:, and similar protocol injections.
          </li>
          <li>Converts non-English characters (if configured).</li>
          <li>Applies HTML sanitization.</li>
          <li>Encodes the final value to prevent re-injection.</li>
          <li>
            Replaces the request’s original value with the sanitized result.
          </li>
        </ul>
      </div>
    </section>
  );
};

export default BehaviorDoc;
